/**
 * The blank, ungated template files offered on the /free/* template pages
 * (SEO plan row 15: "template" searches are won by pages that hand over a
 * real file) and the permits and inspection registers on
 * /free/permits-and-inspection-registers (SEO plan row 24).
 *
 * This is the single list of which blank templates exist and where their
 * files are served from. The files themselves are GENERATED — never edit them
 * by hand:
 *
 *   npx tsx scripts/generate-free-templates.ts
 *
 * renders the wording in lib/marketing/free-template-content.ts to a PDF
 * (Playwright's Chromium) and an editable Word .docx per template, writes them
 * to public/free-templates/, and records their sizes
 * (lib/marketing/free-template-file-sizes.generated.ts) and hashes
 * (lib/free-templates/build-lock.json). __tests__/lib/free-templates.test.ts
 * fails if the files drift from the wording or the renderers.
 *
 * Kept small and data-only because a client component (the download card)
 * imports it.
 */

export const FREE_TEMPLATE_IDS = [
  "rams",
  "method-statement",
  "risk-assessment",
  "coshh",
  "toolbox-talk",
  "site-induction",
  "cpp",
  // Permits and inspection registers (SEO plan row 24), listed together on
  // /free/permits-and-inspection-registers.
  "hot-work-permit",
  "confined-space-permit",
  "permit-to-dig",
  "roof-work-permit",
  "electrical-isolation-permit",
  "scaffold-inspection-register",
  "excavation-inspection-register",
  "ladder-pre-use-check",
  "mewp-pre-use-check",
  "plant-pre-use-check",
  // Offered on their guides only (SEO growth plan, group 1), not on the
  // permits hub.
  "temporary-works-register",
  "welfare-setup-checklist",
  "principal-contractor-checklist",
  // Offered on the asbestos guide only (SEO wave 3, row 7).
  "asbestos-stop-work-checklist",
  // Seasonal checklists on their own /free pages (SEO wave 2, rows 13 and 14).
  "christmas-shutdown-checklist",
  "january-restart-checklist",
  // Emergency plan on its own /free page (SEO wave 3, Workstream D row 12).
  "emergency-plan",
] as const;

export type FreeTemplateId = (typeof FREE_TEMPLATE_IDS)[number];

/** The permit forms, in the order the permits hub lists them. */
export const FREE_PERMIT_TEMPLATE_IDS = [
  "hot-work-permit",
  "confined-space-permit",
  "permit-to-dig",
  "roof-work-permit",
  "electrical-isolation-permit",
] as const satisfies readonly FreeTemplateId[];

/** The inspection registers and pre-use checks, in hub order. */
export const FREE_REGISTER_TEMPLATE_IDS = [
  "scaffold-inspection-register",
  "excavation-inspection-register",
  "ladder-pre-use-check",
  "mewp-pre-use-check",
  "plant-pre-use-check",
] as const satisfies readonly FreeTemplateId[];

export type FreeTemplateFormat = "pdf" | "docx";

export const FREE_TEMPLATE_FORMATS: readonly FreeTemplateFormat[] = ["pdf", "docx"];

/** Public directory (under public/) the generated files are served from. */
export const FREE_TEMPLATE_PUBLIC_DIR = "free-templates";

/**
 * `label` reads in a sentence: "a blank {label} template".
 * `fileBase` is the stable public file name, without the extension. Renaming
 * one breaks every link to it, so do not.
 * `includes` names an extra sheet in the file, shown on the download card.
 */
export const FREE_TEMPLATE_CATALOG: Readonly<
  Record<FreeTemplateId, { label: string; fileBase: string; includes?: string }>
> = {
  rams: { label: "RAMS", fileBase: "blank-rams-template" },
  "method-statement": {
    label: "method statement",
    fileBase: "blank-method-statement-template",
  },
  "risk-assessment": {
    label: "risk assessment",
    fileBase: "blank-risk-assessment-template",
  },
  coshh: { label: "COSHH assessment", fileBase: "blank-coshh-assessment-template" },
  "toolbox-talk": {
    label: "toolbox talk",
    fileBase: "blank-toolbox-talk-template",
    includes: "Includes an attendance sign-off sheet.",
  },
  "site-induction": {
    label: "site induction",
    fileBase: "blank-site-induction-template",
    includes: "Includes an induction record sheet.",
  },
  cpp: {
    label: "construction phase plan",
    fileBase: "blank-construction-phase-plan-template",
  },
  "hot-work-permit": {
    label: "hot work permit",
    fileBase: "blank-hot-work-permit-template",
    includes: "Includes a pre-start checklist and a fire watch record.",
  },
  "confined-space-permit": {
    label: "confined space entry permit",
    fileBase: "blank-confined-space-entry-permit-template",
    includes: "Includes an atmosphere test record and an entry log.",
  },
  "permit-to-dig": {
    label: "permit to dig",
    fileBase: "blank-permit-to-dig-template",
    includes: "Includes an underground services checklist.",
  },
  "roof-work-permit": {
    label: "roof work permit",
    fileBase: "blank-roof-work-permit-template",
  },
  "electrical-isolation-permit": {
    label: "electrical isolation permit",
    fileBase: "blank-electrical-isolation-permit-template",
  },
  "scaffold-inspection-register": {
    label: "scaffold inspection register",
    fileBase: "blank-scaffold-inspection-register-template",
    includes: "Includes an inspection checklist.",
  },
  "excavation-inspection-register": {
    label: "excavation inspection register",
    fileBase: "blank-excavation-inspection-register-template",
    includes: "Includes an inspection checklist.",
  },
  "ladder-pre-use-check": {
    label: "ladder pre-use check",
    fileBase: "blank-ladder-pre-use-check-template",
    includes: "Includes a detailed inspection record.",
  },
  "mewp-pre-use-check": {
    label: "MEWP pre-use check",
    fileBase: "blank-mewp-pre-use-check-template",
    includes: "Includes a fault log.",
  },
  "plant-pre-use-check": {
    label: "plant pre-use check",
    fileBase: "blank-plant-pre-use-check-template",
    includes: "Includes weekly checks and a fault log.",
  },
  "temporary-works-register": {
    label: "temporary works register",
    fileBase: "blank-temporary-works-register-template",
  },
  "welfare-setup-checklist": {
    label: "welfare set-up checklist",
    fileBase: "welfare-setup-checklist",
  },
  "principal-contractor-checklist": {
    label: "principal contractor checklist",
    fileBase: "principal-contractor-checklist",
  },
  "asbestos-stop-work-checklist": {
    label: "asbestos stop-work checklist",
    fileBase: "asbestos-stop-work-checklist",
  },
  "christmas-shutdown-checklist": {
    label: "Christmas shutdown site security checklist",
    fileBase: "christmas-shutdown-site-security-checklist",
  },
  "january-restart-checklist": {
    label: "January restart checklist",
    fileBase: "january-site-restart-checklist",
  },
  "emergency-plan": {
    label: "site emergency plan",
    fileBase: "blank-site-emergency-plan-template",
    includes: "Includes a briefing record sheet.",
  },
};

export const FREE_TEMPLATE_CONTENT_TYPES: Readonly<Record<FreeTemplateFormat, string>> = {
  pdf: "application/pdf",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
};

/** File name, e.g. "blank-rams-template.pdf". */
export function freeTemplateFileName(id: FreeTemplateId, format: FreeTemplateFormat): string {
  return `${FREE_TEMPLATE_CATALOG[id].fileBase}.${format}`;
}

/** Site-relative URL, e.g. "/free-templates/blank-rams-template.pdf". */
export function freeTemplateHref(id: FreeTemplateId, format: FreeTemplateFormat): string {
  return `/${FREE_TEMPLATE_PUBLIC_DIR}/${freeTemplateFileName(id, format)}`;
}

/**
 * Trade RAMS templates (SEO plan row 25), one per trade with a toolbox talk
 * group (lib/marketing/trade-rams-templates.ts). Stable public file name
 * without the extension, e.g. "rams-template-roofers". Renaming breaks links.
 */
export function tradeRamsTemplateFileBase(tradeSlug: string): string {
  return `rams-template-${tradeSlug}`;
}

/** Site-relative URL, e.g. "/free-templates/rams-template-roofers.pdf". */
export function tradeRamsTemplateHref(tradeSlug: string, format: FreeTemplateFormat): string {
  return `/${FREE_TEMPLATE_PUBLIC_DIR}/${tradeRamsTemplateFileBase(tradeSlug)}.${format}`;
}

/** "104 KB": whole kilobytes (1 KB = 1024 bytes), never below 1 KB. */
export function formatFileSize(bytes: number): string {
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}
